import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { adminApi, getApiErrorMessage } from '../services/adminApi'
import { Alert, Button, Checkbox, FormActions, FormSection, Input, PageHeader, Select, Spinner, Textarea } from '../components/ui'

const emptyRange = () => ({ entity_type: 'pessoas', min_quantity: 1, max_quantity: 100, price: 0, is_incremental: 0 })
const empty = () => ({ name: '', description: '', ranges: [emptyRange()] })

function RangeEditor({ ranges, onChange }) {
  const update = (index, field, value) => {
    onChange(ranges.map((range, currentIndex) => currentIndex === index ? { ...range, [field]: value } : range))
  }

  return (
    <div className="space-y-4">
      {ranges.map((range, index) => (
        <div key={index} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-brand-deep">Faixa {index + 1}</p>
            {ranges.length > 1 && (
              <Button variant="ghost" className="text-red-600 hover:bg-red-50 hover:text-red-700" icon="fa-trash" onClick={() => onChange(ranges.filter((_, itemIndex) => itemIndex !== index))}>Remover</Button>
            )}
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <Select label="Tipo" value={range.entity_type} onChange={(e) => update(index, 'entity_type', e.target.value)}>
              <option value="pessoas">Pessoas</option>
              <option value="equipamentos">Equipamentos</option>
              <option value="clube">Módulo Clube</option>
            </Select>
            <Input label="Quantidade mínima" type="number" min="0" value={range.min_quantity ?? ''} onChange={(e) => update(index, 'min_quantity', e.target.value)} required />
            <Input label="Quantidade máxima" type="number" min="0" placeholder="Sem limite" value={range.max_quantity ?? ''} onChange={(e) => update(index, 'max_quantity', e.target.value)} hint="Vazio significa sem limite." />
            <Input label="Preço (R$)" type="number" min="0" step="0.01" value={range.price ?? ''} onChange={(e) => update(index, 'price', e.target.value)} required />
            <Checkbox className="self-end" label="Preço incremental" checked={Boolean(range.is_incremental)} onChange={(e) => update(index, 'is_incremental', e.target.checked ? 1 : 0)} />
          </div>
        </div>
      ))}
      <Button variant="outline" icon="fa-plus" onClick={() => onChange([...ranges, emptyRange()])}>Adicionar faixa</Button>
    </div>
  )
}

export default function PriceForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const editing = Boolean(id)
  const [form, setForm] = useState(empty)
  const [loading, setLoading] = useState(editing)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!editing) return
    let active = true
    adminApi.listPrices()
      .then(({ data }) => {
        const table = (data.data || []).find((item) => String(item.id) === String(id))
        if (!table) throw new Error('Tabela de preços não encontrada.')
        if (active) {
          setForm({
            name: table.name || '',
            description: table.description || '',
            ranges: (table.ranges?.length ? table.ranges : [emptyRange()]).map((range) => ({
              entity_type: range.entity_type,
              min_quantity: range.min_quantity,
              max_quantity: range.max_quantity,
              price: range.price,
              is_incremental: range.is_incremental ? 1 : 0,
            })),
          })
        }
      })
      .catch((err) => active && setError(getApiErrorMessage(err, 'Não foi possível carregar a tabela.')))
      .finally(() => active && setLoading(false))
    return () => { active = false }
  }, [editing, id])

  async function submit(event) {
    event.preventDefault()
    if (saving) return
    if (!form.name.trim()) {
      setError('Informe o nome da tabela.')
      return
    }
    setSaving(true)
    setError('')
    const body = {
      name: form.name.trim(),
      description: form.description,
      ranges: form.ranges.map((range) => ({
        entity_type: range.entity_type,
        min_quantity: Number(range.min_quantity) || 0,
        max_quantity: range.max_quantity === '' || range.max_quantity == null ? null : Number(range.max_quantity),
        price: Number(range.price) || 0,
        is_incremental: range.is_incremental ? 1 : 0,
      })),
    }
    try {
      if (editing) await adminApi.updatePrice(id, body)
      else await adminApi.createPrice(body)
      navigate('/prices', { replace: true, state: { message: editing ? 'Tabela de preços atualizada.' : 'Tabela de preços criada.' } })
    } catch (err) {
      setError(getApiErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <Spinner label="Carregando tabela de preços…" />

  return (
    <form onSubmit={submit} className="space-y-6">
      <PageHeader eyebrow="Comercial" title={editing ? 'Editar tabela de preços' : 'Nova tabela de preços'} description="Configure as faixas de cobrança para pessoas, equipamentos e Módulo Clube." icon="fa-tags" />
      {error && <Alert type="error">{error}</Alert>}
      <FormSection title="Identificação" description="Nome e contexto de uso desta tabela." icon="fa-pen-to-square">
        <div className="grid gap-4 lg:grid-cols-2">
          <Input label="Nome *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus />
          <Textarea label="Descrição" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
      </FormSection>
      <FormSection title="Faixas de preço" description="Defina quantidades, valores e modo de cálculo." icon="fa-layer-group">
        <RangeEditor ranges={form.ranges} onChange={(ranges) => setForm({ ...form, ranges })} />
      </FormSection>
      <FormActions onCancel={() => navigate('/prices')} saving={saving} submitLabel={editing ? 'Salvar alterações' : 'Criar tabela'} />
    </form>
  )
}
